import { useEffect, useRef } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { AssignmentIndRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StepHeader from './StepHeader';

export interface IcpValues {
    icpManagesFor: string;
    icpAccounts: string;
    icpSocialTeam: string;
    icpCurrentSetup: string;
}

/**
 * Four questions, in order. Each asks for a count or an observable fact rather than an identity,
 * because identity answers come back aspirational: someone who posts only for themselves still
 * picks "agency". Nothing here computes a score. The four raw answers are all we store, and the
 * ICP tier is derived downstream in PostHog so its weights can be retuned without a deploy.
 */
export const ICP_QUESTIONS: {
    field: keyof IcpValues;
    /** PostHog person property this answer is stored under. */
    personProperty: string;
    labelKey: string;
    options: { value: string; labelKey: string }[];
}[] = [
    {
        field: 'icpManagesFor',
        personProperty: 'icp_manages_for',
        labelKey: 'onboarding.icp.manages-for.label',
        options: [
            { value: 'clients', labelKey: 'onboarding.icp.manages-for.option.clients' },
            { value: 'own-brands', labelKey: 'onboarding.icp.manages-for.option.own-brands' },
            { value: 'both', labelKey: 'onboarding.icp.manages-for.option.both' },
            { value: 'self', labelKey: 'onboarding.icp.manages-for.option.self' },
        ],
    },
    {
        field: 'icpAccounts',
        personProperty: 'icp_accounts',
        labelKey: 'onboarding.icp.accounts.label',
        options: [
            { value: '1', labelKey: 'onboarding.icp.accounts.option.1' },
            { value: '2-5', labelKey: 'onboarding.icp.accounts.option.2-5' },
            { value: '6-15', labelKey: 'onboarding.icp.accounts.option.6-15' },
            { value: '15+', labelKey: 'onboarding.icp.accounts.option.15+' },
        ],
    },
    {
        field: 'icpSocialTeam',
        personProperty: 'icp_social_team',
        labelKey: 'onboarding.icp.social-team.label',
        options: [
            { value: 'solo', labelKey: 'onboarding.icp.social-team.option.solo' },
            { value: '2-5', labelKey: 'onboarding.icp.social-team.option.2-5' },
            { value: '6-20', labelKey: 'onboarding.icp.social-team.option.6-20' },
            { value: '20+', labelKey: 'onboarding.icp.social-team.option.20+' },
        ],
    },
    {
        field: 'icpCurrentSetup',
        personProperty: 'icp_current_setup',
        labelKey: 'onboarding.icp.current-setup.label',
        options: [
            { value: 'one-tool', labelKey: 'onboarding.icp.current-setup.option.one-tool' },
            { value: 'several-tools', labelKey: 'onboarding.icp.current-setup.option.several-tools' },
            { value: 'manual', labelKey: 'onboarding.icp.current-setup.option.manual' },
            { value: 'none', labelKey: 'onboarding.icp.current-setup.option.none' },
        ],
    },
];

/** How long the tapped chip stays visibly selected before the next question comes in. */
const ADVANCE_DELAY_MS = 200;

interface IcpStepProps {
    values: IcpValues;
    /** Question on screen. Owned by the parent so the dialog footer can drive back / progress. */
    index: number;
    onAnswer: (field: keyof IcpValues, value: string) => void;
    onAdvance: () => void;
}

/**
 * The ICP questions, one per screen. Tapping an option answers and moves on by itself,
 * so the whole step is five taps with no Continue button.
 */
const IcpStep = ({ values, index, onAnswer, onAdvance }: IcpStepProps) => {
    const { t } = useTranslation();
    const advanceTimer = useRef<ReturnType<typeof setTimeout>>();
    const activeQuestionRef = useRef<HTMLDivElement>(null);

    // Direction of the last move, so going back animates back. Read during render while the ref
    // still holds the previous index, then caught up in the effect below.
    const previousIndex = useRef(index);
    const goingBack = index < previousIndex.current;

    useEffect(() => {
        // The chip you just activated is hidden the moment the question changes, which drops
        // keyboard focus to the body. Hand it to the new question instead, so tabbing carries on
        // from here and screen readers announce what is now being asked.
        if (previousIndex.current !== index) {
            activeQuestionRef.current?.focus({ preventScroll: true });
        }
        previousIndex.current = index;
    }, [index]);

    useEffect(() => () => clearTimeout(advanceTimer.current), []);

    const handleSelect = (field: keyof IcpValues, value: string) => {
        onAnswer(field, value);
        // Retime rather than queue, so a double tap lands as one answer and one advance.
        clearTimeout(advanceTimer.current);
        advanceTimer.current = setTimeout(onAdvance, ADVANCE_DELAY_MS);
    };

    return (
        <Stack data-id="onboarding-icp-step">
            <Stack alignItems="center" py={3} px={2}>
                <StepHeader
                    icon={<AssignmentIndRounded fontSize="large" />}
                    circleSx={{ color: 'primary.contrastText', bgcolor: 'primary.light' }}
                    title={t('onboarding.icp.title')}
                    description={t('onboarding.icp.description')}
                />
            </Stack>
            {/* Every question sits in the same grid cell, so the dialog keeps the height of the
                tallest one and doesn't jump between questions whatever the locale does to wrapping. */}
            <Box sx={{ display: 'grid', px: 1, pb: 1 }}>
                {ICP_QUESTIONS.map(({ field, labelKey, options }, questionIndex) => {
                    const active = questionIndex === index;

                    return (
                        <Stack
                            key={field}
                            ref={active ? activeQuestionRef : undefined}
                            tabIndex={active ? -1 : undefined}
                            role="group"
                            aria-labelledby={`icp-question-${field}`}
                            gap={2}
                            alignItems="center"
                            sx={{
                                gridArea: '1 / 1',
                                // Also keeps the off-screen questions out of the tab order.
                                visibility: active ? 'visible' : 'hidden',
                                // Focus only ever lands here programmatically, so no ring to show.
                                outline: 'none',
                                '@keyframes icp-question-in-forward': {
                                    from: { opacity: 0, transform: 'translateX(12px)' },
                                    to: { opacity: 1, transform: 'none' },
                                },
                                '@keyframes icp-question-in-back': {
                                    from: { opacity: 0, transform: 'translateX(-12px)' },
                                    to: { opacity: 1, transform: 'none' },
                                },
                                animation: active
                                    ? `icp-question-in-${goingBack ? 'back' : 'forward'} 180ms ease-out`
                                    : 'none',
                                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                            }}
                        >
                            <Typography
                                id={`icp-question-${field}`}
                                variant="subtitle1"
                                fontWeight="medium"
                                textAlign="center"
                            >
                                {t(labelKey)}
                            </Typography>
                            <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="center">
                                {options.map(({ value, labelKey: optionLabelKey }) => (
                                    <Chip
                                        key={value}
                                        data-id={`onboarding-icp-option-${field}-${value}`}
                                        label={t(optionLabelKey)}
                                        onClick={() => handleSelect(field, value)}
                                        color={values[field] === value ? 'primary' : 'default'}
                                        variant={values[field] === value ? 'filled' : 'outlined'}
                                        clickable
                                        sx={{ height: 40, borderRadius: 5, '& .MuiChip-label': { px: 2 } }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    );
                })}
            </Box>
        </Stack>
    );
};

export default IcpStep;
